/**
 * org-rename — the pure gate + outcome logic behind `RenameOrgDialog`.
 *
 * Renaming an org is a PROD-facing platform-admin mutation whose first real
 * consumer is the go-live plan's **P-TAG** phase (`ai-docs/prod-beta-golive-plan.md`
 * §5): prefix every currently-live prod org with `[TEST] ` so real beta firms
 * are unmistakable beside the mock data. Everything here is deliberately pure
 * so the gate can be tested without a DOM or a server.
 *
 * ## What a rename actually touches (traced 2026-08-04, not assumed)
 *
 * The wire is `PATCH /api/platform/orgs/:orgId` with `{name}`; `UpdateOrgSchema`
 * caps it at 1..200 chars and `updateOrg()` writes `$set.name` and nothing else.
 * So at the DB layer a rename is a pure display-name change. Downstream it is
 * NOT purely cosmetic, and an operator renaming a live org should know which:
 *
 * - **`{{org.name}}` is a live prompt token** (`lexserver/src/lib/prompts/
 *   prompt-builder.ts` ORG_TOKEN_KEYS + `defaults.ts`). It resolves at build
 *   time, not provision time — so the agent's system prompt reflects the new
 *   name on the very next turn. For P-TAG that is the desired effect.
 * - **Invitation emails interpolate it at SEND time** (`orgName` across the
 *   `email/templates/invitation/*` locales) — subject and body both.
 * - **Already-provisioned mailboxes keep the OLD display name.** The system
 *   sender is `Lexiun - <orgName>`, but `createSystemInboxResolver` returns on
 *   the `findInboxByAddress` fast path and only calls `buildSystemDisplayName`
 *   on the cold provision path. Nothing rewrites an existing inbox — the same
 *   caveat `UpdateOrgSchema` already documents for `orgShort`.
 * - **Clerk is untouched, and cannot desync.** lexserver has no Clerk
 *   organization mutation at all (only `create-clerk-user` /
 *   `deactivate-clerk-user`), so there is no org display name over there for
 *   this write to drift from.
 * - **Slugs never change.** Slugs are storage-path-bearing; `UpdateOrgSchema`
 *   has no slug field, so the rename physically cannot move a workspace. The
 *   dialog shows the slug read-only and uses it as the confirm phrase.
 */

/** Mirrors `UpdateOrgSchema.name` in `lexserver/src/db/models/organization.ts`. */
export const ORG_NAME_MIN = 1;
export const ORG_NAME_MAX = 200;

/** The P-TAG marker. Trailing space is part of it — `[TEST] Foo`, not `[TEST]Foo`. */
export const TEST_PREFIX = "[TEST] ";

export type NameValidation =
  | { ok: true; value: string }
  | { ok: false; reason: string };

/**
 * Validate a candidate display name against the server's own bounds.
 *
 * Trims first: the server would accept `"  "` as 2 chars, but a
 * whitespace-only org name is a support incident, not a rename.
 */
export function validateOrgName(raw: string): NameValidation {
  const value = raw.trim();
  if (value.length < ORG_NAME_MIN) {
    return { ok: false, reason: "Name cannot be empty." };
  }
  if (value.length > ORG_NAME_MAX) {
    return {
      ok: false,
      reason: `Name is ${value.length} characters; the server accepts at most ${ORG_NAME_MAX}.`,
    };
  }
  return { ok: true, value };
}

/** Already tagged? Compared on the trimmed value so stray spaces don't fool it. */
export function hasTestPrefix(name: string): boolean {
  return name.trim().startsWith(TEST_PREFIX.trim());
}

/**
 * Add the `[TEST] ` marker. **Idempotent** — the P-TAG operator works through
 * a list of orgs and re-tagging an already-tagged one must not produce
 * `[TEST] [TEST] Foo`.
 */
export function withTestPrefix(name: string): string {
  const value = name.trim();
  if (hasTestPrefix(value)) return value;
  return `${TEST_PREFIX}${value}`;
}

/** Remove a leading `[TEST]` marker (with or without its trailing space). */
export function withoutTestPrefix(name: string): string {
  const value = name.trim();
  if (!hasTestPrefix(value)) return value;
  return value.slice(TEST_PREFIX.trim().length).trim();
}

export type RenameGate =
  | { canSubmit: true; nextName: string }
  | { canSubmit: false; reason: string };

/**
 * Decide whether the Rename button is live.
 *
 * The confirm phrase is the org's **slug**, not its name — the slug is the
 * stable, unique, storage-bearing identity, and asking an operator to retype
 * the name that is about to stop existing proves nothing about *which* org
 * they are on. Same rationale as `PermanentDeleteOrgDialog`.
 */
export function evaluateRenameGate(input: {
  currentName: string;
  nextName: string;
  slug: string;
  typedConfirm: string;
  busy: boolean;
}): RenameGate {
  if (input.busy) return { canSubmit: false, reason: "Save in progress." };

  const validation = validateOrgName(input.nextName);
  if (!validation.ok) return { canSubmit: false, reason: validation.reason };

  if (validation.value === input.currentName.trim()) {
    return { canSubmit: false, reason: "That is already the current name." };
  }

  if (input.typedConfirm.trim() !== input.slug) {
    return {
      canSubmit: false,
      reason: `Type the org slug (${input.slug}) to confirm which org you are renaming.`,
    };
  }

  return { canSubmit: true, nextName: validation.value };
}

export type RenameOutcome =
  /** Server echoed the name we asked for. */
  | { kind: "applied"; name: string }
  /** 200, but the name came back unchanged — the write did not take. */
  | { kind: "not-applied"; requested: string; actual: string }
  /** 200, but the name is neither the old nor the requested one. */
  | { kind: "diverged"; requested: string; actual: string }
  /** 200 with no readable org in the body — we cannot claim anything. */
  | { kind: "unverifiable"; requested: string };

/**
 * Classify what the server ACTUALLY did, instead of trusting the 200.
 *
 * This lane has been burned by exactly this shape before (`classifyEmailSave`,
 * commit `c874ed6fc`): the update route runs the patch through a Zod schema
 * that silently STRIPS fields it does not know, so a server that cannot honour
 * the write still answers `200 {ok:true}` with an untouched row. A rename that
 * reports success without having happened is worse than a visible failure —
 * during P-TAG it would leave a real-looking prod org sitting untagged while
 * the checklist says it is done.
 *
 * Compare what came back; say "not applied" when it did not.
 */
export function classifyRenameSave(
  requested: string,
  previousName: string,
  returned: { name?: unknown } | null | undefined,
): RenameOutcome {
  const want = requested.trim();
  const actual = typeof returned?.name === "string" ? returned.name : null;

  if (actual === null) return { kind: "unverifiable", requested: want };
  if (actual === want) return { kind: "applied", name: actual };
  if (actual === previousName.trim()) {
    return { kind: "not-applied", requested: want, actual };
  }
  return { kind: "diverged", requested: want, actual };
}

/** Operator-facing sentence for a non-applied outcome. `null` when it applied. */
export function describeRenameOutcome(outcome: RenameOutcome): string | null {
  if (outcome.kind === "applied") return null;
  if (outcome.kind === "not-applied") {
    return (
      `The server accepted the request but the name came back unchanged ` +
      `("${outcome.actual}"). The rename did NOT take — treat this org as still untagged.`
    );
  }
  if (outcome.kind === "diverged") {
    return (
      `Saved, but the org now reads "${outcome.actual}" rather than the ` +
      `"${outcome.requested}" you asked for — someone else may have edited it at the same time. Reload before retrying.`
    );
  }
  return (
    `The server answered OK but returned no organization, so the rename could ` +
    `not be verified. Reload and check the name before recording this org as done.`
  );
}
